/**
 * Cart Subscription Reinforcement
 *
 * Handles switching one-time cart items to subscriptions using
 * Shopify's Cart API. Preserves quantity and updates cart UI.
 */

(function () {
  'use strict';

  const SELECTORS = {
    config: '[data-cart-subscription-config]',
    inlineReinforcement: '[data-cart-subscription-reinforcement]',
    consolidatedReinforcement: '[data-cart-subscription-reinforcement-consolidated]',
    switchCta: '[data-subscription-switch-cta]',
    switchAllCta: '[data-subscription-switch-all-cta]',
    reinforcementText: '[data-reinforcement-text]',
    ctaLabel: '[data-cta-label]',
  };

  /**
   * Get configuration from hidden config element
   */
  function getConfig() {
    const configEl = document.querySelector(SELECTORS.config);
    if (!configEl) return null;

    const enabled = configEl.dataset.enabled === 'true';
    if (!enabled) return null;

    let productsConfig = {};
    try {
      productsConfig = JSON.parse(configEl.dataset.productsConfig || '{}');
    } catch (e) {
      console.warn('Cart subscription reinforcement: Invalid products config');
    }

    return {
      enabled: true,
      defaultText: configEl.dataset.defaultText || '',
      defaultCta: configEl.dataset.defaultCta || '',
      showPerLine: configEl.dataset.showPerLine === 'true',
      products: productsConfig,
    };
  }

  /**
   * Get text for a specific product (metafield priority)
   */
  function getTextForProduct(config, productHandle, type) {
    const productConfig = config.products[productHandle];

    if (productConfig) {
      if (type === 'text' && productConfig.text) return productConfig.text;
      if (type === 'cta' && productConfig.cta) return productConfig.cta;
    }

    return type === 'text' ? config.defaultText : config.defaultCta;
  }

  /**
   * Apply metafield text overrides to inline reinforcement elements
   */
  function applyTextOverrides(config) {
    const reinforcements = document.querySelectorAll(SELECTORS.inlineReinforcement);

    reinforcements.forEach((el) => {
      const productHandle = el.dataset.productHandle;
      if (!productHandle) return;

      const textEl = el.querySelector(SELECTORS.reinforcementText);
      const ctaEl = el.querySelector(SELECTORS.ctaLabel);

      if (textEl) {
        textEl.textContent = getTextForProduct(config, productHandle, 'text');
      }
      if (ctaEl) {
        ctaEl.textContent = getTextForProduct(config, productHandle, 'cta');
      }
    });
  }

  /**
   * Switch a single line item to subscription
   */
  async function switchToSubscription(lineKey, variantId, sellingPlanId, quantity) {
    // Step 1: Remove the one-time item
    const removeBody = JSON.stringify({
      updates: { [lineKey]: 0 },
    });

    const removeResponse = await fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: removeBody,
    });

    if (!removeResponse.ok) {
      throw new Error('Failed to remove item from cart');
    }

    // Step 2: Add the subscription item
    const addBody = JSON.stringify({
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: quantity,
          selling_plan: parseInt(sellingPlanId, 10),
          properties: {
            _subscription_source: 'cart-upsell',
          },
        },
      ],
    });

    const addResponse = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: addBody,
    });

    if (!addResponse.ok) {
      throw new Error('Failed to add subscription item');
    }

    return await addResponse.json();
  }


  /**
   * Handle single item CTA click
   */
  async function handleSingleSwitch(event) {
    const button = event.currentTarget;
    const reinforcement = button.closest(SELECTORS.inlineReinforcement);

    if (!reinforcement) return;

    const { lineKey, variantId, sellingPlanId, quantity } = reinforcement.dataset;

    if (!lineKey || !variantId || !sellingPlanId) {
      console.error('Cart subscription: Missing required data attributes');
      return;
    }

    // Show loading state
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    // Track the click event
    if (window.CartTracking) {
      window.CartTracking.trackSubscriptionSwitch({
        variantId: variantId,
        productTitle: reinforcement.dataset.productTitle || '',
        productHandle: reinforcement.dataset.productHandle || '',
        quantity: parseInt(quantity, 10),
        sellingPlanId: sellingPlanId,
        type: 'single',
      });
    }

    try {
      await switchToSubscription(lineKey, variantId, sellingPlanId, parseInt(quantity, 10));

      // Show success state briefly
      reinforcement.classList.add('cart-subscription-reinforcement--success');

      // Trigger cart update event for section re-render
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-subscription-reinforcement',
          variantId: variantId,
        });
      } else {
        // Fallback: reload cart sections
        window.location.reload();
      }
    } catch (error) {
      console.error('Cart subscription switch failed:', error);
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');

      // Show error state
      reinforcement.classList.add('cart-subscription-reinforcement--error');
      setTimeout(() => {
        reinforcement.classList.remove('cart-subscription-reinforcement--error');
      }, 3000);
    }
  }

  /**
   * Handle "switch all" CTA click
   */
  async function handleSwitchAll(event) {
    const button = event.currentTarget;
    const reinforcement = button.closest(SELECTORS.consolidatedReinforcement);

    if (!reinforcement) return;

    const eligibleItemsStr = reinforcement.dataset.eligibleItems;
    if (!eligibleItemsStr) return;

    // Parse: "key1:variantId1:spId1:qty1,key2:variantId2:spId2:qty2"
    const items = eligibleItemsStr.split(',').map((item) => {
      const [lineKey, variantId, sellingPlanId, quantity] = item.split(':');
      return { lineKey, variantId, sellingPlanId, quantity: parseInt(quantity, 10) };
    });

    if (items.length === 0) return;

    // Show loading state
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    // Track the click event for "switch all"
    if (window.CartTracking) {
      window.CartTracking.trackSubscriptionSwitch({
        variantId: items.map((i) => i.variantId).join(','),
        productTitle: 'Multiple items',
        productHandle: '',
        quantity: items.reduce((sum, i) => sum + i.quantity, 0),
        sellingPlanId: items.map((i) => i.sellingPlanId).join(','),
        type: 'all',
      });
    }

    try {
      // Process items sequentially to avoid race conditions
      for (const item of items) {
        await switchToSubscription(item.lineKey, item.variantId, item.sellingPlanId, item.quantity);
      }

      // Trigger cart update
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-subscription-reinforcement',
        });
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Cart subscription switch all failed:', error);
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    }
  }


  /**
   * Attach event listeners
   */
  function attachListeners() {
    // Single item CTAs - use event delegation to handle dynamic elements
    document.querySelectorAll(SELECTORS.switchCta).forEach((button) => {
      // Remove existing listener to prevent duplicates
      button.removeEventListener('click', handleSingleSwitch);
      button.addEventListener('click', handleSingleSwitch);
    });

    // Switch all CTA
    document.querySelectorAll(SELECTORS.switchAllCta).forEach((button) => {
      button.removeEventListener('click', handleSwitchAll);
      button.addEventListener('click', handleSwitchAll);
    });

  }

  /**
   * Re-initialize after cart update (section re-render)
   */
  function reinitialize() {
    const config = getConfig();
    if (!config) return;

    applyTextOverrides(config);
    attachListeners();
  }

  /**
   * Initialize
   */
  function init() {
    const config = getConfig();
    if (!config) return;

    // Apply metafield text overrides
    applyTextOverrides(config);

    // Attach click handlers
    attachListeners();

    // Listen for cart updates to reinitialize
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Skip if we triggered this update
        if (event.source === 'cart-subscription-reinforcement') {
          // Still reinitialize after a delay to catch the re-rendered DOM
          setTimeout(reinitialize, 200);
          return;
        }
        // Delay to allow DOM to update
        setTimeout(reinitialize, 100);
      });
    }

    // Also observe for dynamic section changes
    const observer = new MutationObserver((mutations) => {
      let shouldReinit = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (
                node.matches?.(SELECTORS.inlineReinforcement) ||
                node.querySelector?.(SELECTORS.inlineReinforcement) ||
                node.matches?.(SELECTORS.consolidatedReinforcement) ||
                node.querySelector?.(SELECTORS.consolidatedReinforcement)
              ) {
                shouldReinit = true;
                break;
              }
            }
          }
        }
        if (shouldReinit) break;
      }

      if (shouldReinit) {
        reinitialize();
      }
    });

    // Observe cart containers
    const cartContainers = document.querySelectorAll('cart-items, cart-drawer-items, .cart__blocks');
    cartContainers.forEach((container) => {
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
